import { SlateStatus } from "./SlateIndicator";

export interface SlateMismatch {
    field: string;
    expected?: string | boolean | number | null;
    actual?: string | boolean | number | null;
}

export interface SlateStatusCounts {
    synced: number;
    require_sync: number;
    not_exist: number;
    unknown: number;
    total: number;
}

export interface SlateSyncMeta {
    lastSyncAt?: string | null;
    lastFullSyncAt?: string | null;
    lastSyncCount?: number;
    lastSkippedCount?: number;
    lastFullSyncTotal?: number;
    inProgress?: boolean;
    lastError?: string | null;
    submitInProgress?: boolean;
    submitTotal?: number;
    submitStartedAt?: string | null;
    lastSubmitAt?: string | null;
    lastSubmitSucceeded?: number;
    lastSubmitFailed?: number;
    lastSubmitError?: string | null;
}

const SLATE_FIELD_LABELS: Record<string, string> = {
    licenseType: "License type",
    territory: "Territory",
    start: "Playback start",
    end: "Playback end",
    formatProfile: "Format profile",
    licenseRightsDescription: "License rights",
    channelIdentity: "Channel identity",
    priceCategory: "Price category",
    download: "Download",
    rentalDuration: "Rental duration",
    watchDuration: "Watch duration",
    announceDate: "Announce date",
    brandingRights: "Branding rights",
    window: "License window",
};

export function getSlateFieldLabel(field: string): string {
    return SLATE_FIELD_LABELS[field] || field;
}

/** Drop synthetic "License window / not_found" rows from older cache entries. */
export function filterDisplayableSlateMismatches(
    mismatches: SlateMismatch[] | undefined | null
): SlateMismatch[] {
    if (!mismatches?.length) return [];
    return mismatches.filter(
        (mismatch) =>
            !(
                mismatch.field === "window" &&
                String(mismatch.actual ?? "").toLowerCase() === "not_found"
            )
    );
}

export function formatSlateMismatchValue(
    value: string | boolean | number | null | undefined
): string {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "True" : "False";
    const text = String(value).trim();
    if (!text) return "—";
    const date = new Date(text);
    if (!Number.isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(text)) {
        return date.toLocaleString();
    }
    return text;
}

export function countSlateStatuses(
    rows: Array<{ slate_status?: SlateStatus }>
): SlateStatusCounts {
    const counts: SlateStatusCounts = {
        synced: 0,
        require_sync: 0,
        not_exist: 0,
        unknown: 0,
        total: rows.length,
    };

    for (const row of rows) {
        const status = row.slate_status;
        if (status === "synced") counts.synced += 1;
        else if (status === "require_sync") counts.require_sync += 1;
        else if (status === "not_exist") counts.not_exist += 1;
        else counts.unknown += 1;
    }

    return counts;
}

export function formatSlatePageSummary(counts: SlateStatusCounts): string {
    if (!counts.total) return "No titles on this page";
    const parts: string[] = [];
    if (counts.synced) parts.push(`${counts.synced} synced`);
    if (counts.require_sync) parts.push(`${counts.require_sync} out of sync`);
    if (counts.not_exist) parts.push(`${counts.not_exist} not in Slate`);
    if (counts.unknown) parts.push(`${counts.unknown} pending`);
    return parts.join(" · ");
}

export function formatSlateLastRefresh(
    lastSyncAt: string | null | undefined,
    inProgress?: boolean
): string {
    if (inProgress) return "Refreshing…";
    if (!lastSyncAt) return "Not refreshed yet";
    const when = new Date(lastSyncAt);
    if (Number.isNaN(when.getTime())) return "Not refreshed yet";

    const diffMs = Date.now() - when.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    return when.toLocaleString();
}

export function summarizeSlateRefreshEntries(
    entries: Array<{ slateStatus?: string }>
): { message: string; variant: "success" | "warning" | "info" } {
    if (!entries.length) {
        return { message: "Slate refresh completed.", variant: "info" };
    }

    const counts = countSlateStatuses(
        entries.map((entry) => ({ slate_status: entry.slateStatus as SlateStatus }))
    );
    const parts: string[] = [];
    if (counts.synced) parts.push(`${counts.synced} synced`);
    if (counts.require_sync) parts.push(`${counts.require_sync} out of sync`);
    if (counts.not_exist) parts.push(`${counts.not_exist} not in Slate`);

    const message = `Slate refreshed: ${parts.join(", ") || "no changes"}.`;
    const variant =
        counts.require_sync || counts.not_exist ? "warning" : "success";
    return { message, variant };
}

export function formatSlateSubmitError(
    errors: Array<{ message?: string; code?: string }> | undefined
): string {
    if (!errors?.length) return "Submit to Slate failed.";
    return errors
        .map((error) => error?.message || error?.code)
        .filter(Boolean)
        .slice(0, 3)
        .join("; ");
}

export function summarizeSlateSubmitResults(
    data: {
        submitted?: number;
        succeeded?: number;
        failed?: number;
        results?: Array<{
            refId?: string;
            success?: boolean;
            stage?: string;
            errors?: Array<{ message?: string; code?: string }>;
        }>;
    } | null
): { message: string; variant: "success" | "warning" | "error" | "info" } {
    if (!data) {
        return { message: "Submit to Slate completed.", variant: "info" };
    }

    const submitted = data.submitted ?? data.results?.length ?? 0;
    const succeeded = data.succeeded ?? 0;
    const failed = data.failed ?? Math.max(0, submitted - succeeded);

    if (!submitted) {
        return { message: "No titles submitted to Slate.", variant: "info" };
    }
    if (failed === 0) {
        return {
            message: `Submitted ${succeeded} title${succeeded === 1 ? "" : "s"} to Slate.`,
            variant: "success",
        };
    }

    const firstFailure = (data.results || []).find((row) => !row.success);
    const detail = firstFailure
        ? formatSlateSubmitError(firstFailure.errors)
        : undefined;
    const base = `Submitted ${succeeded}/${submitted} to Slate (${failed} failed)`;
    return {
        message: detail ? `${base}: ${detail}` : `${base}.`,
        variant: succeeded > 0 ? "warning" : "error",
    };
}
